import { useEffect, useState } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import { TimeModal, adminPausedOf, formatTime } from '../../AdminDashboardV3/DiagnosticMonitor';
import { DIAGNOSTIC_IDS, diagnosticLabel } from '../../Diagnostic/diagnosticAvailability';
import { Modal, ModalHeader, Button } from './ui';
import { DLG } from './dialogStyles';
import { diagAdminApi, fetchClasses, fetchDiagnosticLive } from '../data/api';

/* Pause / unpause a student's diagnostic, or set their section time, from
   Buffy's chat (2026-10-06). It loads the student's live status the way the
   Diagnostic page does (get_monitor_detail, school-scoped) and offers only the
   control the page would show: Pause while the timer runs, Unpause once paused,
   Set time (the page's own TimeModal) while they're in the test. Same calls as
   the page (adminDiagnosticControls). */
const isLive = (row) => !!row?.timerStatus && row.status !== 'Done';

export default function DiagnosticControlDialog({ mode, student, test, auth, classId, onClose, onDone }) {
  const preset = DIAGNOSTIC_IDS[Number(test) - 1] || null;
  const [state, setState] = useState({ loading: true });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  // Find the test the student is in right now (Sandy 2026-10-06: Buffy asked
  // "are they taking a different diagnostic?" when it could just look). The
  // named test is checked first; if the student isn't live there, all three
  // are checked, the way the Diagnostic page picks which test to open on.
  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    (async () => {
      const classes = await fetchClasses(auth);
      const cls = (classes || []).find((c) => c.id === classId || c.classID === classId);
      const schoolName = cls?.school || undefined;
      const rowFor = async (assignmentId) => {
        const live = await fetchDiagnosticLive(auth, { assignmentId, schoolName, studentIds: [student.userId] });
        return (live?.students || []).find((s) => s.studentId === student.userId) || null;
      };
      if (preset) {
        const row = await rowFor(preset);
        if (isLive(row)) { if (alive) setState({ id: preset, row, schoolName }); return; }
      }
      const rows = await Promise.all(DIAGNOSTIC_IDS.map((id) => rowFor(id).catch(() => null)));
      const at = rows.findIndex(isLive);
      if (!alive) return;
      if (at >= 0) setState({ id: DIAGNOSTIC_IDS[at], row: rows[at], schoolName, movedFrom: preset && preset !== DIAGNOSTIC_IDS[at] ? preset : null });
      else setState({ id: preset, row: preset ? rows[DIAGNOSTIC_IDS.indexOf(preset)] : null, schoolName });
    })().catch(() => { if (alive) setState({ failed: true }); });
    return () => { alive = false; };
  }, [preset, auth, classId, student.userId]);

  const assignmentId = state.id || null;
  const testLabel = assignmentId ? diagnosticLabel(assignmentId) : 'a diagnostic';
  const row = state.row;
  const inTest = isLive(row);
  const run = async (body, note, label) => {
    setWorking(true);
    setError(null);
    try {
      await diagAdminApi(auth, { ...body, assignmentId, studentId: student.userId, ...(state.schoolName ? { schoolName: state.schoolName } : {}) });
      onDone(note, label);
      onClose();
    } catch (e) {
      setError(e?.message || 'That didn’t go through. Try again.');
    } finally {
      setWorking(false);
    }
  };

  if (mode === 'time' && inTest && !error) {
    return (
      <TimeModal
        s={{ ...row, name: student.name }}
        busy={working}
        working={working}
        onClose={onClose}
        onSet={(minutes) => run(
          { action: 'set_time', newTimeRemaining: minutes * 60 },
          `Set ${student.name}'s ${testLabel} section time to ${minutes} minutes.`,
          `Time set for ${student.name}`,
        )}
      />
    );
  }

  const paused = row && adminPausedOf(row);
  let status;
  if (state.loading) status = `Checking ${student.name}'s diagnostics…`;
  else if (state.failed) status = 'Couldn’t load this class’s diagnostics. Try again in a moment.';
  else if (!inTest) {
    status = preset && row?.status === 'Done'
      ? `${student.name} already finished ${testLabel} and isn’t in another diagnostic right now.`
      : `${student.name} isn’t taking ${preset ? `${testLabel} or any other diagnostic` : 'a diagnostic'} right now.`;
  }
  else if (paused) status = `Paused · ${formatTime(row.timeRemaining)} left.`;
  else if (row.timerStatus === 'active') status = `In the test now · ${formatTime(row.timeRemaining)} left. They pause within about 40 seconds.`;
  else status = `${student.name}’s timer isn’t running right now.`;
  const canPause = mode === 'pause' && inTest && !paused && row.timerStatus === 'active';
  const canUnpause = mode === 'pause' && inTest && paused;
  return (
    <Modal open onClose={working ? () => {} : onClose} size="sm">
      <ModalHeader title={mode === 'time' ? 'Set section time' : assignmentId ? `${testLabel} timer` : 'Diagnostic timer'} sub={student.name} pad={DLG.head} onClose={working ? () => {} : onClose} />
      <div className={DLG.body}>
        <p role="status" className="m-0 text-[13px] text-neutral-700 leading-relaxed flex items-center gap-2">
          {state.loading && <Loader2 size={14} className="animate-spin flex-none" aria-hidden="true" />}
          {status}
        </p>
        {state.movedFrom && inTest && (
          <p className="mt-2 mb-0 text-[12.5px] text-neutral-500">Not in {diagnosticLabel(state.movedFrom)} right now; they’re in {testLabel}.</p>
        )}
        {mode === 'time' && !state.loading && !state.failed && !inTest && (
          <p className="mt-2 mb-0 text-[12.5px] text-neutral-500">Section time can only be set while they&apos;re in the test.</p>
        )}
        {error && <p role="alert" className="mt-[12px] mb-0 text-[12.5px] font-semibold text-error-600">{error}</p>}
      </div>
      <div className={DLG.foot}>
        <Button variant="secondary" size="dlg" onClick={onClose} disabled={working}>{canPause || canUnpause ? 'Cancel' : 'Close'}</Button>
        {canPause && (
          <button
            type="button"
            disabled={working}
            onClick={() => run({ action: 'individual_pause' }, `Paused ${student.name}'s ${testLabel}; they pause within about 40 seconds.`, `${student.name} pausing`)}
            className="inline-flex items-center justify-center gap-1.5 h-10 px-[16px] whitespace-nowrap rounded-[8px] border-0 ring-1 ring-inset ring-error-200 bg-white hover:bg-error-50 text-error-600 text-sm font-semibold cursor-pointer disabled:opacity-50"
          >
            {working ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Pause size={13} aria-hidden="true" />} Pause
          </button>
        )}
        {canUnpause && (
          <button
            type="button"
            disabled={working}
            onClick={() => run({ action: 'individual_unpause' }, `Unpaused ${student.name}'s ${testLabel}.`, `${student.name} unpaused`)}
            className="inline-flex items-center justify-center gap-1.5 h-10 px-[16px] whitespace-nowrap rounded-[8px] border-0 bg-success-600 hover:bg-success-700 text-white text-sm font-semibold cursor-pointer disabled:opacity-50"
          >
            {working ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Play size={13} aria-hidden="true" />} Unpause
          </button>
        )}
      </div>
    </Modal>
  );
}
